import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

// @mui icons
import {
    AccountBalanceWallet,
    Add,
    Business,
    CheckCircle,
    CompareArrows,
    FilterAlt,
    HourglassTop,
    ReceiptLong,
    Refresh,
    Search,
    WarningAmber,
} from "@mui/icons-material";

// @mui material components
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Card,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    Grid,
    InputAdornment,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    TextField,
    Typography,
} from "@mui/material";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";

// Material Dashboard 2 React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

// Finance language
import {
    financeText,
    getFinanceLanguage,
    saveFinanceLanguage,
} from "finance-language";

import {
    COLORS,
    cardSx,
} from "../shared/FinanceUiKit";

const API_URL =
    process.env.REACT_APP_API_URL ||
    "http://localhost:8080/api";

const FIELD_HEIGHT =
    48;

const commonFieldSx = {
    "& .MuiInputBase-root": {
        height:
        FIELD_HEIGHT,

        minHeight:
        FIELD_HEIGHT,

        borderRadius:
            "10px",

        backgroundColor:
        COLORS.white,

        boxSizing:
            "border-box",
    },

    "& .MuiOutlinedInput-root": {
        height:
        FIELD_HEIGHT,

        minHeight:
        FIELD_HEIGHT,

        borderRadius:
            "10px",

        backgroundColor:
        COLORS.white,

        boxSizing:
            "border-box",
    },

    "& .MuiOutlinedInput-input": {
        height:
        FIELD_HEIGHT,

        boxSizing:
            "border-box",

        display:
            "flex",

        alignItems:
            "center",

        paddingTop:
            "0 !important",

        paddingBottom:
            "0 !important",
    },

    "& .MuiInputLabel-root": {
        background:
        COLORS.white,

        px:
            0.35,
    },
};

const commonSelectSx = {
    ...commonFieldSx,

    "& .MuiSelect-select": {
        height:
            `${FIELD_HEIGHT}px !important`,

        minHeight:
            "0 !important",

        boxSizing:
            "border-box",

        display:
            "flex",

        alignItems:
            "center",

        paddingTop:
            "0 !important",

        paddingBottom:
            "0 !important",
    },
};

const commonAutocompleteSx = {
    width: "100%",

    "& .MuiOutlinedInput-root": {
        height: `${FIELD_HEIGHT}px !important`,
        minHeight: `${FIELD_HEIGHT}px !important`,
        borderRadius: "10px",
        backgroundColor: COLORS.white,

        paddingTop: "0 !important",
        paddingBottom: "0 !important",
        paddingLeft: "9px !important",
        paddingRight: "38px !important",

        display: "flex",
        alignItems: "center",
        boxSizing: "border-box",
    },

    "& .MuiAutocomplete-inputRoot": {
        height: `${FIELD_HEIGHT}px !important`,
        minHeight: `${FIELD_HEIGHT}px !important`,
        paddingTop: "0 !important",
        paddingBottom: "0 !important",
        display: "flex",
        alignItems: "center",
        flexWrap: "nowrap",
    },

    "& .MuiAutocomplete-input": {
        height: "20px !important",
        minHeight: "20px !important",

        padding: "0 4px !important",

        lineHeight: "20px !important",

        display: "block",
        alignSelf: "center",

        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",

        boxSizing: "border-box",
    },

    "& .MuiAutocomplete-endAdornment": {
        top: "50%",
        right: "9px",
        transform: "translateY(-50%)",
    },

    "& .MuiAutocomplete-clearIndicator": {
        padding: "3px",
    },

    "& .MuiAutocomplete-popupIndicator": {
        padding: "3px",
    },

    "& .MuiInputLabel-root": {
        backgroundColor: COLORS.white,
        paddingLeft: "4px",
        paddingRight: "4px",
    },

    "& .MuiInputLabel-root.MuiInputLabel-shrink": {
        transform: "translate(14px, -9px) scale(0.75)",
    },
};

// ============================================================
// CONSTANTS
// ============================================================

const PARTY_TYPES = [
    {
        value:
            "customer",

        label:
            "Customer",
    },

    {
        value:
            "supplier",

        label:
            "Service Provider",
    },
];

const CURRENCIES = [
    "TRY",
    "USD",
    "EUR",
    "GBP",
];

const STATUSES = [
    {
        value:
            "draft",

        label:
            "Draft",
    },

    {
        value:
            "sent",

        label:
            "Sent",
    },

    {
        value:
            "waiting_response",

        label:
            "Waiting Response",
    },

    {
        value:
            "confirmed",

        label:
            "Confirmed",
    },

    {
        value:
            "disputed",

        label:
            "Disputed",
    },

    {
        value:
            "cancelled",

        label:
            "Cancelled",
    },
];

const DEFAULT_FORM = {
    partyType:
        "customer",

    partyId:
        "",

    currency:
        "EUR",

    periodEnd:
        "",
};

// ============================================================
// HELPERS
// ============================================================

const toNumber = (
    value
) => {
    const number =
        Number(
            value
        );

    return Number.isFinite(
        number
    )
        ? number
        : 0;
};

const formatMoney = (
    value,
    currency = "EUR",
    language = "en"
) => {
    try {
        return new Intl.NumberFormat(
            language === "tr"
                ? "tr-TR"
                : "en-US",
            {
                style:
                    "currency",

                currency:
                    currency ||
                    "EUR",

                minimumFractionDigits:
                    2,

                maximumFractionDigits:
                    2,
            }
        ).format(
            toNumber(
                value
            )
        );
    } catch (
        error
        ) {
        return `${toNumber(value).toFixed(2)} ${currency || ""}`;
    }
};

const formatDate = (
    value,
    language = "en"
) => {
    if (
        !value
    ) {
        return "-";
    }

    const date =
        new Date(
            value
        );

    if (
        Number.isNaN(
            date.getTime()
        )
    ) {
        return "-";
    }

    return date.toLocaleDateString(
        language === "tr"
            ? "tr-TR"
            : "en-GB"
    );
};

const getStatusConfig = (
    status
) => {
    const normalized =
        String(
            status ||
            "draft"
        ).toLowerCase();

    const config = {
        draft: {
            label:
                "Draft",

            background:
                "#F2F4F7",

            textColor:
                "#344054",
        },

        sent: {
            label:
                "Sent",

            background:
            COLORS.blueLight,

            textColor:
            COLORS.blue,
        },

        waiting_response: {
            label:
                "Waiting Response",

            background:
            COLORS.amberLight,

            textColor:
            COLORS.amber,
        },

        confirmed: {
            label:
                "Confirmed",

            background:
            COLORS.greenLight,

            textColor:
            COLORS.green,
        },

        disputed: {
            label:
                "Disputed",

            background:
            COLORS.redLight,

            textColor:
            COLORS.red,
        },

        cancelled: {
            label:
                "Cancelled",

            background:
                "#F2F4F7",

            textColor:
            COLORS.subtext,
        },
    };

    return (
        config[
            normalized
            ] ||
        config.draft
    );
};

const extractRows = (
    payload
) => {
    const candidates = [
        payload,

        payload?.data,

        payload?.records,

        payload?.results,

        payload?.customers,

        payload?.suppliers,

        payload?.serviceProviders,

        payload?.globalAgents,

        payload?.global_agents,

        payload
            ?.data
            ?.records,

        payload
            ?.data
            ?.results,

        payload
            ?.data
            ?.customers,

        payload
            ?.data
            ?.suppliers,

        payload
            ?.data
            ?.serviceProviders,

        payload
            ?.data
            ?.globalAgents,

        payload
            ?.data
            ?.global_agents,
    ];

    return (
        candidates.find(
            Array.isArray
        ) ||
        []
    );
};

const getPartyId = (
    party
) =>
    party?._id ||
    party?.id ||
    party
        ?.attributes
        ?._id ||
    party
        ?.attributes
        ?.id ||
    party?.customerId ||
    party?.supplierId ||
    party?.serviceProviderId ||
    "";

const getPartyName = (
    party
) =>
    party?.companyName ||
    party?.customerName ||
    party?.supplierName ||
    party?.serviceProviderName ||
    party?.name ||
    party?.nameKey ||
    party
        ?.attributes
        ?.companyName ||
    party
        ?.attributes
        ?.customerName ||
    party
        ?.attributes
        ?.supplierName ||
    party
        ?.attributes
        ?.serviceProviderName ||
    party
        ?.attributes
        ?.name ||
    party
        ?.attributes
        ?.nameKey ||
    "";

const normalizeParty = (
    party,
    partyType
) => ({
    ...party,

    _id:
        getPartyId(
            party
        ),

    name:
        getPartyName(
            party
        ),

    partyType,
});

// ============================================================
// COMPONENTS
// ============================================================

function SummaryCard({
                         title,
                         value,
                         icon,
                         iconBackground,
                         iconColor,
                         helper,
                     }) {
    return (
        <Card
            sx={
                cardSx
            }
        >
            <MDBox
                p={2.5}
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                height="100%"
            >
                <MDBox>
                    <MDTypography
                        variant="caption"
                        sx={{
                            color:
                            COLORS.subtext,

                            fontWeight:
                                700,

                            letterSpacing:
                                ".03em",
                        }}
                    >
                        {title}
                    </MDTypography>

                    <MDTypography
                        variant="h4"
                        fontWeight="bold"
                        mt={0.6}
                        sx={{
                            color:
                            COLORS.text,
                        }}
                    >
                        {value}
                    </MDTypography>

                    {helper ? (
                        <MDTypography
                            variant="caption"
                            sx={{
                                color:
                                COLORS.subtext,

                                display:
                                    "block",

                                mt:
                                    0.5,
                            }}
                        >
                            {helper}
                        </MDTypography>
                    ) : null}
                </MDBox>

                <MDBox
                    width={48}
                    height={48}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    borderRadius="8px"
                    sx={{
                        background:
                        iconBackground,

                        color:
                        iconColor,

                        flexShrink:
                            0,
                    }}
                >
                    {icon}
                </MDBox>
            </MDBox>
        </Card>
    );
}

// ============================================================
// PAGE
// ============================================================

function Reconciliations() {
    const navigate =
        useNavigate();

    const [
        financeLanguage,
        setFinanceLanguageState,
    ] = useState(
        getFinanceLanguage
    );

    const [
        reconciliations,
        setReconciliations,
    ] = useState(
        []
    );

    const [
        parties,
        setParties,
    ] = useState(
        []
    );

    const [
        loading,
        setLoading,
    ] = useState(
        false
    );

    const [
        partyLoading,
        setPartyLoading,
    ] = useState(
        false
    );

    const [
        calculateLoading,
        setCalculateLoading,
    ] = useState(
        false
    );

    const [
        saveLoading,
        setSaveLoading,
    ] = useState(
        false
    );

    const [
        openModal,
        setOpenModal,
    ] = useState(
        false
    );

    const [
        calculation,
        setCalculation,
    ] = useState(
        null
    );

    const [
        error,
        setError,
    ] = useState(
        ""
    );

    const [
        form,
        setForm,
    ] = useState(
        DEFAULT_FORM
    );

    const [
        filters,
        setFilters,
    ] = useState({
        search:
            "",

        partyType:
            "",

        currency:
            "",

        status:
            "",
    });

    // ============================================================
    // LANGUAGE
    // ============================================================

    const changeFinanceLanguage = (
        event
    ) => {
        const nextLanguage =
            event.target.value ===
            "tr"
                ? "tr"
                : "en";

        saveFinanceLanguage(
            nextLanguage
        );

        setFinanceLanguageState(
            nextLanguage
        );
    };

    // ============================================================
    // AUTH
    // ============================================================

    const getAuthHeaders =
        () => {
            const token =
                localStorage.getItem(
                    "token"
                );

            return token
                ? {
                    Authorization:
                        `Bearer ${token}`,
                }
                : {};
        };

    // ============================================================
    // GENERIC LOADER
    // ============================================================

    const loadFirstAvailable =
        async (
            paths
        ) => {
            for (
                const path
                of paths
                ) {
                try {
                    const response =
                        await axios.get(
                            `${API_URL}${path}`,
                            {
                                headers:
                                    getAuthHeaders(),
                            }
                        );

                    return extractRows(
                        response
                            ?.data
                    );
                } catch (
                    requestError
                    ) {
                    console.warn(
                        `${path} request failed`,
                        requestError
                    );
                }
            }

            return [];
        };

    // ============================================================
    // RECONCILIATIONS
    // ============================================================

    const fetchReconciliations =
        async () => {
            try {
                setLoading(
                    true
                );

                setError(
                    ""
                );

                const response =
                    await axios.get(
                        `${API_URL}/finance/reconciliations`,
                        {
                            headers:
                                getAuthHeaders(),
                        }
                    );

                const data =
                    response
                        ?.data
                        ?.data ||
                    response
                        ?.data ||
                    [];

                setReconciliations(
                    Array.isArray(
                        data
                    )
                        ? data
                        : []
                );
            } catch (
                requestError
                ) {
                console.error(
                    "Reconciliations fetch error:",
                    requestError
                );

                setError(
                    financeText(
                        "Reconciliations could not be fetched."
                    )
                );

                setReconciliations(
                    []
                );
            } finally {
                setLoading(
                    false
                );
            }
        };

    // ============================================================
    // PARTY LOAD
    // ============================================================

    const fetchParties =
        async (
            partyType
        ) => {
            try {
                setPartyLoading(
                    true
                );

                setError(
                    ""
                );

                let rows =
                    [];

                if (
                    partyType ===
                    "customer"
                ) {
                    rows =
                        await loadFirstAvailable([
                            "/customer",
                            "/customers",
                        ]);

                    rows =
                        rows.map(
                            (
                                party
                            ) =>
                                normalizeParty(
                                    party,
                                    "customer"
                                )
                        );
                } else if (
                    partyType ===
                    "supplier"
                ) {
                    rows =
                        await loadFirstAvailable([
                            "/service-provider",
                            "/serviceProvider",
                            "/finance/suppliers",
                            "/suppliers",
                        ]);

                    rows =
                        rows.map(
                            (
                                party
                            ) =>
                                normalizeParty(
                                    party,
                                    "supplier"
                                )
                        );
                }

                const normalized =
                    rows
                        .filter(
                            (
                                party
                            ) =>
                                Boolean(
                                    party._id &&
                                    party.name
                                )
                        )
                        .sort(
                            (
                                a,
                                b
                            ) =>
                                a.name.localeCompare(
                                    b.name,
                                    financeLanguage ===
                                    "tr"
                                        ? "tr"
                                        : "en"
                                )
                        );

                setParties(
                    normalized
                );
            } catch (
                requestError
                ) {
                console.error(
                    "Reconciliation party load error:",
                    requestError
                );

                setParties(
                    []
                );

                setError(
                    financeText(
                        "Accounts could not be fetched."
                    )
                );
            } finally {
                setPartyLoading(
                    false
                );
            }
        };

    // ============================================================
    // EFFECTS
    // ============================================================

    useEffect(() => {
        fetchReconciliations();
    }, []);

    useEffect(() => {
        if (
            !openModal
        ) {
            return;
        }

        setParties(
            []
        );

        setForm(
            (
                current
            ) => ({
                ...current,

                partyId:
                    "",
            })
        );

        setCalculation(
            null
        );

        fetchParties(
            form.partyType
        );
    }, [
        form.partyType,
        openModal,
    ]);

    // ============================================================
    // MODAL
    // ============================================================

    const handleOpenModal =
        () => {
            setForm({
                ...DEFAULT_FORM,
            });

            setParties(
                []
            );

            setCalculation(
                null
            );

            setError(
                ""
            );

            setOpenModal(
                true
            );
        };

    const handleCloseModal =
        () => {
            if (
                calculateLoading ||
                saveLoading
            ) {
                return;
            }

            setOpenModal(
                false
            );

            setCalculation(
                null
            );

            setParties(
                []
            );

            setError(
                ""
            );
        };

    // ============================================================
    // FORM
    // ============================================================

    const handleFormChange = (
        field,
        value
    ) => {
        setForm(
            (
                current
            ) => ({
                ...current,

                [field]:
                value,
            })
        );

        setCalculation(
            null
        );

        setError(
            ""
        );
    };

    // ============================================================
    // FILTER
    // ============================================================

    const handleFilterChange = (
        field,
        value
    ) => {
        setFilters(
            (
                current
            ) => ({
                ...current,

                [field]:
                value,
            })
        );
    };

    const clearFilters =
        () => {
            setFilters({
                search:
                    "",

                partyType:
                    "",

                currency:
                    "",

                status:
                    "",
            });
        };

    // ============================================================
    // VALIDATION
    // ============================================================

    const validateForm =
        () => {
            if (
                !form.partyType
            ) {
                return financeText(
                    "Party type is required."
                );
            }

            if (
                !form.partyId
            ) {
                return financeText(
                    "Account is required."
                );
            }

            if (
                !form.currency
            ) {
                return financeText(
                    "Currency is required."
                );
            }

            if (
                !form.periodEnd
            ) {
                return financeText(
                    "Balance date is required."
                );
            }

            const endDate =
                new Date(
                    form.periodEnd
                );

            if (
                Number.isNaN(
                    endDate.getTime()
                )
            ) {
                return financeText(
                    "Invalid balance date."
                );
            }

            return "";
        };

    // ============================================================
    // CALCULATE
    // ============================================================

    const handleCalculate =
        async () => {
            const validationError =
                validateForm();

            if (
                validationError
            ) {
                setError(
                    validationError
                );

                return;
            }

            try {
                setCalculateLoading(
                    true
                );

                setError(
                    ""
                );

                const response =
                    await axios.post(
                        `${API_URL}/finance/reconciliations/calculate`,
                        {
                            partyType:
                            form.partyType,

                            partyId:
                            form.partyId,

                            currency:
                            form.currency,

                            periodEnd:
                            form.periodEnd,
                        },
                        {
                            headers:
                                getAuthHeaders(),
                        }
                    );

                const data =
                    response
                        ?.data
                        ?.data ||
                    response
                        ?.data ||
                    null;

                setCalculation(
                    data
                );
            } catch (
                requestError
                ) {
                console.error(
                    "Reconciliation calculation error:",
                    requestError
                );

                setError(
                    requestError
                        ?.response
                        ?.data
                        ?.message ||
                    financeText(
                        "Reconciliation could not be calculated."
                    )
                );
            } finally {
                setCalculateLoading(
                    false
                );
            }
        };

    // ============================================================
    // SAVE
    // ============================================================

    const handleSaveDraft =
        async () => {
            if (
                !calculation
            ) {
                return;
            }

            try {
                setSaveLoading(
                    true
                );

                setError(
                    ""
                );

                const response =
                    await axios.post(
                        `${API_URL}/finance/reconciliations`,
                        {
                            partyType:
                            form.partyType,

                            partyId:
                            form.partyId,

                            currency:
                            form.currency,

                            periodEnd:
                            form.periodEnd,
                        },
                        {
                            headers:
                                getAuthHeaders(),
                        }
                    );

                const created =
                    response
                        ?.data
                        ?.data ||
                    response
                        ?.data;

                const createdId =
                    created
                        ?._id ||
                    created
                        ?.id;

                setOpenModal(
                    false
                );

                setCalculation(
                    null
                );

                setParties(
                    []
                );

                await fetchReconciliations();

                if (
                    createdId
                ) {
                    navigate(
                        `/invoice/finance/reconciliations/${createdId}`
                    );
                }
            } catch (
                requestError
                ) {
                console.error(
                    "Reconciliation save error:",
                    requestError
                );

                setError(
                    requestError
                        ?.response
                        ?.data
                        ?.message ||
                    financeText(
                        "Reconciliation could not be saved."
                    )
                );
            } finally {
                setSaveLoading(
                    false
                );
            }
        };

    // ============================================================
    // STATS
    // ============================================================

    const stats =
        useMemo(
            () => {
                const total =
                    reconciliations.length;

                const confirmed =
                    reconciliations.filter(
                        (
                            item
                        ) =>
                            item.status ===
                            "confirmed"
                    ).length;

                const waiting =
                    reconciliations.filter(
                        (
                            item
                        ) =>
                            [
                                "sent",
                                "waiting_response",
                            ].includes(
                                item.status
                            )
                    ).length;

                const disputed =
                    reconciliations.filter(
                        (
                            item
                        ) =>
                            item.status ===
                            "disputed"
                    ).length;

                return {
                    total,
                    confirmed,
                    waiting,
                    disputed,
                };
            },
            [
                reconciliations,
            ]
        );

    // ============================================================
    // FILTERED DATA
    // ============================================================

    const filteredData =
        useMemo(
            () => {
                const search =
                    String(
                        filters.search ||
                        ""
                    )
                        .trim()
                        .toLocaleLowerCase(
                            financeLanguage ===
                            "tr"
                                ? "tr-TR"
                                : "en-US"
                        );

                return reconciliations.filter(
                    (
                        item
                    ) => {
                        const partyType =
                            String(
                                item.partyType ||
                                ""
                            ).toLowerCase();

                        const currency =
                            String(
                                item.currency ||
                                ""
                            ).toUpperCase();

                        const status =
                            String(
                                item.status ||
                                ""
                            ).toLowerCase();

                        const partyName =
                            String(
                                item
                                    ?.snapshot
                                    ?.party
                                    ?.name ||
                                ""
                            ).toLocaleLowerCase(
                                financeLanguage ===
                                "tr"
                                    ? "tr-TR"
                                    : "en-US"
                            );

                        const reconciliationNo =
                            String(
                                item.reconciliationNo ||
                                ""
                            ).toLowerCase();

                        if (
                            filters.partyType &&
                            partyType !==
                            filters.partyType
                        ) {
                            return false;
                        }

                        if (
                            filters.currency &&
                            currency !==
                            filters.currency
                        ) {
                            return false;
                        }

                        if (
                            filters.status &&
                            status !==
                            filters.status
                        ) {
                            return false;
                        }

                        if (
                            search &&
                            !partyName.includes(
                                search
                            ) &&
                            !reconciliationNo.includes(
                                search
                            )
                        ) {
                            return false;
                        }

                        return true;
                    }
                );
            },
            [
                reconciliations,
                filters,
                financeLanguage,
            ]
        );

    // ============================================================
    // TABLE
    // ============================================================

    const tableData =
        useMemo(
            () => ({
                columns: [
                    {
                        Header:
                            financeText(
                                "RECONCILIATION"
                            ),

                        accessor:
                            "reconciliationNo",

                        width:
                            "15%",
                    },

                    {
                        Header:
                            financeText(
                                "ACCOUNT"
                            ),

                        accessor:
                            "partyName",

                        width:
                            "22%",
                    },

                    {
                        Header:
                            financeText(
                                "TYPE"
                            ),

                        accessor:
                            "partyType",

                        width:
                            "12%",
                    },

                    {
                        Header:
                            financeText(
                                "BALANCE DATE"
                            ),

                        accessor:
                            "periodEnd",

                        width:
                            "13%",
                    },

                    {
                        Header:
                            financeText(
                                "CURRENCY"
                            ),

                        accessor:
                            "currency",

                        width:
                            "9%",
                    },

                    {
                        Header:
                            financeText(
                                "BALANCE"
                            ),

                        accessor:
                            "balance",

                        align:
                            "right",

                        width:
                            "13%",
                    },

                    {
                        Header:
                            financeText(
                                "DIFFERENCE"
                            ),

                        accessor:
                            "difference",

                        align:
                            "right",

                        width:
                            "12%",
                    },

                    {
                        Header:
                            financeText(
                                "STATUS"
                            ),

                        accessor:
                            "status",

                        width:
                            "12%",
                    },

                    {
                        Header:
                            financeText(
                                "ACTIONS"
                            ),

                        accessor:
                            "actions",

                        align:
                            "center",

                        width:
                            "7%",
                    },
                ],

                rows:
                    filteredData.map(
                        (
                            item
                        ) => {
                            const statusConfig =
                                getStatusConfig(
                                    item.status
                                );

                            const closingBalance =
                                item
                                    ?.snapshot
                                    ?.closingBalance ??
                                0;

                            const difference =
                                item
                                    ?.differenceAmount ??
                                0;

                            const id =
                                item._id ||
                                item.id;

                            const partyTypeLabel =
                                item.partyType ===
                                "supplier"
                                    ? financeText(
                                        "Service Provider"
                                    )
                                    : financeText(
                                        "Customer"
                                    );

                            return {
                                reconciliationNo: (
                                    <MDTypography
                                        variant="button"
                                        fontWeight="bold"
                                        sx={{
                                            color:
                                            COLORS.blue,
                                        }}
                                    >
                                        {item.reconciliationNo ||
                                            "-"}
                                    </MDTypography>
                                ),

                                partyName: (
                                    <MDBox>
                                        <MDTypography
                                            variant="button"
                                            fontWeight="bold"
                                            display="block"
                                            sx={{
                                                color:
                                                COLORS.text,
                                            }}
                                        >
                                            {item
                                                    ?.snapshot
                                                    ?.party
                                                    ?.name ||
                                                "-"}
                                        </MDTypography>

                                        <MDTypography
                                            variant="caption"
                                            sx={{
                                                color:
                                                COLORS.subtext,
                                            }}
                                        >
                                            {
                                                partyTypeLabel
                                            }
                                        </MDTypography>
                                    </MDBox>
                                ),

                                partyType:
                                partyTypeLabel,

                                periodEnd: (
                                    <MDTypography
                                        variant="button"
                                        fontWeight="medium"
                                        sx={{
                                            color:
                                            COLORS.text,
                                        }}
                                    >
                                        {formatDate(
                                            item.periodEnd,
                                            financeLanguage
                                        )}
                                    </MDTypography>
                                ),

                                currency: (
                                    <Chip
                                        size="small"
                                        label={
                                            item.currency ||
                                            "-"
                                        }
                                        sx={{
                                            fontWeight:
                                                700,

                                            background:
                                                "#F2F4F7",

                                            color:
                                            COLORS.text,
                                        }}
                                    />
                                ),

                                balance: (
                                    <MDTypography
                                        variant="button"
                                        fontWeight="bold"
                                        sx={{
                                            color:
                                            COLORS.text,
                                        }}
                                    >
                                        {formatMoney(
                                            closingBalance,
                                            item.currency,
                                            financeLanguage
                                        )}
                                    </MDTypography>
                                ),

                                difference: (
                                    <MDTypography
                                        variant="button"
                                        fontWeight="bold"
                                        sx={{
                                            color:
                                                Math.abs(
                                                    difference
                                                ) <
                                                0.01
                                                    ? COLORS.green
                                                    : COLORS.red,
                                        }}
                                    >
                                        {formatMoney(
                                            difference,
                                            item.currency,
                                            financeLanguage
                                        )}
                                    </MDTypography>
                                ),

                                status: (
                                    <Chip
                                        size="small"
                                        label={
                                            financeText(
                                                statusConfig.label
                                            )
                                        }
                                        sx={{
                                            fontWeight:
                                                700,

                                            background:
                                            statusConfig.background,

                                            color:
                                            statusConfig.textColor,

                                            border:
                                                "none",
                                        }}
                                    />
                                ),

                                actions: (
                                    <MDButton
                                        variant="text"
                                        color="info"
                                        size="small"
                                        onClick={() =>
                                            navigate(
                                                `/invoice/finance/reconciliations/${id}`
                                            )
                                        }
                                    >
                                        {financeText(
                                            "View"
                                        )}
                                    </MDButton>
                                ),
                            };
                        }
                    ),
            }),
            [
                filteredData,
                navigate,
                financeLanguage,
            ]
        );

    // ============================================================
    // CALCULATION DATA
    // ============================================================

    const transactionTable =
        calculation
            ?.transactions ||
        [];

    const selectedParty =
        useMemo(
            () =>
                parties.find(
                    (
                        party
                    ) =>
                        String(
                            party._id
                        ) ===
                        String(
                            form.partyId
                        )
                ) ||
                null,
            [
                parties,
                form.partyId,
            ]
        );

    // ============================================================
    // RENDER
    // ============================================================

    return (
        <DashboardLayout>
            <DashboardNavbar />

            <MDBox
                pt={6}
                pb={3}
                sx={{
                    minHeight:
                        "100vh",

                    background:
                    COLORS.page,

                    mx: {
                        xs:
                            -2,

                        md:
                            -3,
                    },

                    px: {
                        xs:
                            2,

                        md:
                            3,
                    },
                }}
            >
                {/* HERO */}
                <Card
                    sx={{
                        borderRadius:
                            "22px",

                        color:
                        COLORS.white,

                        background:
                            "linear-gradient(125deg, #081A31 0%, #0D2D52 52%, #175CD3 135%)",

                        boxShadow:
                            "0 18px 46px rgba(11,31,58,.22)",

                        overflow:
                            "hidden",

                        position:
                            "relative",

                        mb:
                            3,

                        "&::after": {
                            content:
                                '""',

                            position:
                                "absolute",

                            width:
                                360,

                            height:
                                360,

                            borderRadius:
                                "50%",

                            right:
                                -130,

                            top:
                                -180,

                            background:
                                "rgba(255,255,255,.07)",
                        },

                        "&::before": {
                            content:
                                '""',

                            position:
                                "absolute",

                            width:
                                230,

                            height:
                                230,

                            borderRadius:
                                "50%",

                            right:
                                120,

                            bottom:
                                -170,

                            background:
                                "rgba(11,165,236,.16)",
                        },
                    }}
                >
                    <MDBox
                        p={{
                            xs:
                                2.5,

                            md:
                                3.5,
                        }}
                        position="relative"
                        zIndex={1}
                    >
                        <Grid
                            container
                            spacing={3}
                            alignItems="center"
                        >
                            <Grid
                                item
                                xs={12}
                                lg={7}
                            >
                                <MDBox
                                    display="flex"
                                    alignItems="center"
                                    gap={1}
                                    mb={1}
                                >
                                    <MDBox
                                        width={34}
                                        height={34}
                                        borderRadius="10px"
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="center"
                                        sx={{
                                            background:
                                                "rgba(255,255,255,.12)",
                                        }}
                                    >
                                        <CompareArrows
                                            sx={{
                                                color:
                                                    "#FFFFFF",

                                                fontSize:
                                                    20,
                                            }}
                                        />
                                    </MDBox>

                                    <MDTypography
                                        variant="caption"
                                        fontWeight="bold"
                                        sx={{
                                            color:
                                                "rgba(255,255,255,.72)",

                                            letterSpacing:
                                                ".12em",
                                        }}
                                    >
                                        {financeText(
                                            "ACCOUNT RECONCILIATION"
                                        )}
                                    </MDTypography>
                                </MDBox>

                                <MDTypography
                                    variant="h2"
                                    fontWeight="bold"
                                    sx={{
                                        color:
                                            "#FFFFFF",

                                        lineHeight:
                                            1.12,
                                    }}
                                >
                                    {financeText(
                                        "Reconciliations"
                                    )}
                                </MDTypography>

                                <MDTypography
                                    variant="body2"
                                    mt={1}
                                    sx={{
                                        color:
                                            "rgba(255,255,255,.72)",

                                        maxWidth:
                                            720,
                                    }}
                                >
                                    {financeText(
                                        "Review and confirm balances with customers and service providers from a single financial control center."
                                    )}
                                </MDTypography>
                            </Grid>

                            <Grid
                                item
                                xs={12}
                                lg={5}
                            >
                                <MDBox
                                    p={2}
                                    borderRadius="16px"
                                    sx={{
                                        background:
                                            "rgba(255,255,255,.09)",

                                        border:
                                            "1px solid rgba(255,255,255,.13)",

                                        backdropFilter:
                                            "blur(8px)",
                                    }}
                                >
                                    <MDBox
                                        display="flex"
                                        justifyContent="space-between"
                                        alignItems="center"
                                        mb={1}
                                    >
                                        <MDTypography
                                            variant="caption"
                                            fontWeight="bold"
                                            sx={{
                                                color:
                                                    "rgba(255,255,255,.72)",
                                            }}
                                        >
                                            {financeText(
                                                "TOTAL RECONCILIATIONS"
                                            )}
                                        </MDTypography>

                                        <CompareArrows
                                            sx={{
                                                color:
                                                    "#B2DDFF",
                                            }}
                                        />
                                    </MDBox>

                                    <MDTypography
                                        variant="h3"
                                        fontWeight="bold"
                                        sx={{
                                            color:
                                                "#FFFFFF",
                                        }}
                                    >
                                        {
                                            stats.total
                                        }
                                    </MDTypography>

                                    <MDTypography
                                        variant="caption"
                                        sx={{
                                            color:
                                                "rgba(255,255,255,.68)",
                                        }}
                                    >
                                        {
                                            stats.confirmed
                                        }{" "}
                                        {financeText(
                                            "Confirmed"
                                        )}

                                        {" · "}

                                        {
                                            stats.waiting
                                        }{" "}
                                        {financeText(
                                            "Waiting Response"
                                        )}
                                    </MDTypography>

                                    <MDBox
                                        display="flex"
                                        gap={1}
                                        mt={2}
                                        alignItems="stretch"
                                        flexWrap="wrap"
                                    >
                                        <MDBox
                                            sx={{
                                                display:
                                                    "flex",

                                                alignItems:
                                                    "center",

                                                gap:
                                                    "4px",

                                                p:
                                                    "4px",

                                                background:
                                                    "rgba(255,255,255,.10)",

                                                border:
                                                    "1px solid rgba(255,255,255,.20)",

                                                borderRadius:
                                                    "12px",

                                                flexShrink:
                                                    0,
                                            }}
                                        >
                                            {[
                                                {
                                                    value:
                                                        "en",

                                                    label:
                                                        "EN",

                                                    flag:
                                                        "/flags/gb.svg",
                                                },

                                                {
                                                    value:
                                                        "tr",

                                                    label:
                                                        "TR",

                                                    flag:
                                                        "/flags/tr.svg",
                                                },
                                            ].map(
                                                (
                                                    language
                                                ) => (
                                                    <MDBox
                                                        key={
                                                            language.value
                                                        }
                                                        component="button"
                                                        type="button"
                                                        onClick={() =>
                                                            changeFinanceLanguage({
                                                                target: {
                                                                    value:
                                                                    language.value,
                                                                },
                                                            })
                                                        }
                                                        sx={{
                                                            border:
                                                                "none",

                                                            cursor:
                                                                "pointer",

                                                            display:
                                                                "flex",

                                                            alignItems:
                                                                "center",

                                                            justifyContent:
                                                                "center",

                                                            gap:
                                                                "7px",

                                                            height:
                                                                38,

                                                            minWidth:
                                                                76,

                                                            px:
                                                                1.15,

                                                            borderRadius:
                                                                "8px",

                                                            background:
                                                                financeLanguage ===
                                                                language.value
                                                                    ? "#FFFFFF"
                                                                    : "transparent",

                                                            color:
                                                                financeLanguage ===
                                                                language.value
                                                                    ? COLORS.navy
                                                                    : "#FFFFFF",
                                                        }}
                                                    >
                                                        <MDBox
                                                            component="img"
                                                            src={
                                                                language.flag
                                                            }
                                                            alt={
                                                                language.label
                                                            }
                                                            sx={{
                                                                width:
                                                                    24,

                                                                height:
                                                                    17,

                                                                objectFit:
                                                                    "cover",

                                                                borderRadius:
                                                                    "3px",
                                                            }}
                                                        />

                                                        <MDTypography
                                                            component="span"
                                                            sx={{
                                                                fontSize:
                                                                    "12px",

                                                                fontWeight:
                                                                    800,

                                                                color:
                                                                    "inherit",
                                                            }}
                                                        >
                                                            {
                                                                language.label
                                                            }
                                                        </MDTypography>
                                                    </MDBox>
                                                )
                                            )}
                                        </MDBox>

                                        <MDButton
                                            variant="contained"
                                            onClick={
                                                handleOpenModal
                                            }
                                            sx={{
                                                background:
                                                    "#FFFFFF !important",

                                                color:
                                                    `${COLORS.navy} !important`,

                                                flex:
                                                    1,

                                                minWidth:
                                                    190,
                                            }}
                                        >
                                            <Add sx={{ mr: 0.5 }} />

                                            {financeText(
                                                "New Reconciliation"
                                            )}
                                        </MDButton>

                                        <Button
                                            onClick={
                                                fetchReconciliations
                                            }
                                            sx={{
                                                minWidth:
                                                    44,

                                                color:
                                                    "#FFFFFF",

                                                border:
                                                    "1px solid rgba(255,255,255,.18)",

                                                borderRadius:
                                                    "10px",
                                            }}
                                        >
                                            <Refresh />
                                        </Button>
                                    </MDBox>
                                </MDBox>
                            </Grid>
                        </Grid>
                    </MDBox>
                </Card>

                {/* KPI */}
                <Grid
                    container
                    spacing={2.5}
                    mb={2.5}
                >
                    <Grid item xs={12} sm={6} lg={3}>
                        <SummaryCard
                            title={
                                financeText(
                                    "TOTAL RECONCILIATIONS"
                                )
                            }
                            value={
                                stats.total
                            }
                            helper={
                                financeText(
                                    "All reconciliation records"
                                )
                            }
                            icon={
                                <ReceiptLong />
                            }
                            iconBackground={
                                COLORS.blueLight
                            }
                            iconColor={
                                COLORS.blue
                            }
                        />
                    </Grid>

                    <Grid item xs={12} sm={6} lg={3}>
                        <SummaryCard
                            title={
                                financeText(
                                    "CONFIRMED"
                                )
                            }
                            value={
                                stats.confirmed
                            }
                            helper={
                                financeText(
                                    "Successfully matched"
                                )
                            }
                            icon={
                                <CheckCircle />
                            }
                            iconBackground={
                                COLORS.greenLight
                            }
                            iconColor={
                                COLORS.green
                            }
                        />
                    </Grid>

                    <Grid item xs={12} sm={6} lg={3}>
                        <SummaryCard
                            title={
                                financeText(
                                    "WAITING RESPONSE"
                                )
                            }
                            value={
                                stats.waiting
                            }
                            helper={
                                financeText(
                                    "Sent or pending response"
                                )
                            }
                            icon={
                                <HourglassTop />
                            }
                            iconBackground={
                                COLORS.amberLight
                            }
                            iconColor={
                                COLORS.amber
                            }
                        />
                    </Grid>

                    <Grid item xs={12} sm={6} lg={3}>
                        <SummaryCard
                            title={
                                financeText(
                                    "DISPUTED"
                                )
                            }
                            value={
                                stats.disputed
                            }
                            helper={
                                financeText(
                                    "Requires finance review"
                                )
                            }
                            icon={
                                <WarningAmber />
                            }
                            iconBackground={
                                COLORS.redLight
                            }
                            iconColor={
                                COLORS.red
                            }
                        />
                    </Grid>
                </Grid>

                {error &&
                !openModal ? (
                    <Alert
                        severity="error"
                        sx={{
                            mb:
                                2.5,

                            borderRadius:
                                "8px",
                        }}
                        onClose={() =>
                            setError(
                                ""
                            )
                        }
                    >
                        {error}
                    </Alert>
                ) : null}

                {/* FILTERS */}
                <Card
                    sx={{
                        ...cardSx,

                        mb:
                            2.5,
                    }}
                >
                    <MDBox p={2.5}>
                        <MDBox
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                            mb={2}
                        >
                            <MDBox
                                display="flex"
                                alignItems="center"
                                gap={1}
                            >
                                <MDBox
                                    width={38}
                                    height={38}
                                    borderRadius="11px"
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    sx={{
                                        color:
                                        COLORS.blue,

                                        background:
                                        COLORS.blueLight,

                                        border:
                                            "1px solid #B2DDFF",
                                    }}
                                >
                                    <FilterAlt
                                        sx={{
                                            fontSize:
                                                19,
                                        }}
                                    />
                                </MDBox>

                                <MDBox>
                                    <MDTypography
                                        variant="h6"
                                        fontWeight="bold"
                                        sx={{
                                            color:
                                            COLORS.text,
                                        }}
                                    >
                                        {financeText(
                                            "Reconciliation Filters"
                                        )}
                                    </MDTypography>

                                    <MDTypography
                                        variant="caption"
                                        sx={{
                                            color:
                                            COLORS.subtext,
                                        }}
                                    >
                                        {financeText(
                                            "Search and segment reconciliations by account, type, currency and status."
                                        )}
                                    </MDTypography>
                                </MDBox>
                            </MDBox>

                            <MDButton
                                variant="text"
                                color="info"
                                size="small"
                                onClick={
                                    clearFilters
                                }
                            >
                                <Refresh fontSize="small" />

                                &nbsp;

                                {financeText(
                                    "Reset"
                                )}
                            </MDButton>
                        </MDBox>

                        <Grid
                            container
                            spacing={2}
                            alignItems="stretch"
                        >
                            <Grid
                                item
                                xs={12}
                                md={4}
                            >
                                <TextField
                                    fullWidth
                                    size="small"
                                    label={
                                        financeText(
                                            "Search"
                                        )
                                    }
                                    placeholder={
                                        financeText(
                                            "Account or reconciliation no"
                                        )
                                    }
                                    value={
                                        filters.search
                                    }
                                    onChange={(
                                        event
                                    ) =>
                                        handleFilterChange(
                                            "search",
                                            event.target.value
                                        )
                                    }
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Search
                                                    fontSize="small"
                                                    sx={{
                                                        color:
                                                        COLORS.subtext,
                                                    }}
                                                />
                                            </InputAdornment>
                                        ),
                                    }}
                                    sx={
                                        commonFieldSx
                                    }
                                />
                            </Grid>

                            <Grid
                                item
                                xs={12}
                                md={2.7}
                            >
                                <FormControl
                                    fullWidth
                                    size="small"
                                    sx={
                                        commonSelectSx
                                    }
                                >
                                    <InputLabel>
                                        {financeText(
                                            "Party Type"
                                        )}
                                    </InputLabel>

                                    <Select
                                        value={
                                            filters.partyType
                                        }
                                        label={
                                            financeText(
                                                "Party Type"
                                            )
                                        }
                                        onChange={(
                                            event
                                        ) =>
                                            handleFilterChange(
                                                "partyType",
                                                event.target.value
                                            )
                                        }
                                    >
                                        <MenuItem value="">
                                            {financeText(
                                                "All Parties"
                                            )}
                                        </MenuItem>

                                        {PARTY_TYPES.map(
                                            (
                                                item
                                            ) => (
                                                <MenuItem
                                                    key={
                                                        item.value
                                                    }
                                                    value={
                                                        item.value
                                                    }
                                                >
                                                    {financeText(
                                                        item.label
                                                    )}
                                                </MenuItem>
                                            )
                                        )}
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid
                                item
                                xs={12}
                                md={2.3}
                            >
                                <FormControl
                                    fullWidth
                                    size="small"
                                    sx={
                                        commonSelectSx
                                    }
                                >
                                    <InputLabel>
                                        {financeText(
                                            "Currency"
                                        )}
                                    </InputLabel>

                                    <Select
                                        value={
                                            filters.currency
                                        }
                                        label={
                                            financeText(
                                                "Currency"
                                            )
                                        }
                                        onChange={(
                                            event
                                        ) =>
                                            handleFilterChange(
                                                "currency",
                                                event.target.value
                                            )
                                        }
                                    >
                                        <MenuItem value="">
                                            {financeText(
                                                "All Currencies"
                                            )}
                                        </MenuItem>

                                        {CURRENCIES.map(
                                            (
                                                currency
                                            ) => (
                                                <MenuItem
                                                    key={
                                                        currency
                                                    }
                                                    value={
                                                        currency
                                                    }
                                                >
                                                    {
                                                        currency
                                                    }
                                                </MenuItem>
                                            )
                                        )}
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid
                                item
                                xs={12}
                                md={3}
                            >
                                <FormControl
                                    fullWidth
                                    size="small"
                                    sx={
                                        commonSelectSx
                                    }
                                >
                                    <InputLabel>
                                        {financeText(
                                            "Status"
                                        )}
                                    </InputLabel>

                                    <Select
                                        value={
                                            filters.status
                                        }
                                        label={
                                            financeText(
                                                "Status"
                                            )
                                        }
                                        onChange={(
                                            event
                                        ) =>
                                            handleFilterChange(
                                                "status",
                                                event.target.value
                                            )
                                        }
                                    >
                                        <MenuItem value="">
                                            {financeText(
                                                "All Statuses"
                                            )}
                                        </MenuItem>

                                        {STATUSES.map(
                                            (
                                                item
                                            ) => (
                                                <MenuItem
                                                    key={
                                                        item.value
                                                    }
                                                    value={
                                                        item.value
                                                    }
                                                >
                                                    {financeText(
                                                        item.label
                                                    )}
                                                </MenuItem>
                                            )
                                        )}
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>
                    </MDBox>
                </Card>

                {/* TABLE */}
                <Card
                    sx={
                        cardSx
                    }
                >
                    <MDBox
                        px={3}
                        pt={2.7}
                        pb={1.5}
                    >
                        <MDTypography
                            variant="h6"
                            fontWeight="bold"
                            sx={{
                                color:
                                COLORS.text,
                            }}
                        >
                            {financeText(
                                "Reconciliation Register"
                            )}
                        </MDTypography>

                        <MDTypography
                            variant="caption"
                            sx={{
                                color:
                                COLORS.subtext,
                            }}
                        >
                            {
                                filteredData.length
                            }{" "}
                            {financeText(
                                "records currently displayed"
                            )}
                        </MDTypography>
                    </MDBox>

                    <Divider />

                    {loading ? (
                        <MDBox
                            minHeight={320}
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            justifyContent="center"
                            gap={1.5}
                        >
                            <CircularProgress />

                            <MDTypography
                                variant="button"
                                sx={{
                                    color:
                                    COLORS.subtext,
                                }}
                            >
                                {financeText(
                                    "Loading reconciliations..."
                                )}
                            </MDTypography>
                        </MDBox>
                    ) : (
                        <MDBox
                            sx={{
                                "& .MuiTableContainer-root": {
                                    border:
                                        "none",

                                    overflowX:
                                        "auto",
                                },

                                "& table": {
                                    minWidth:
                                        1150,
                                },

                                "& table thead": {
                                    background:
                                        "#F8FAFC",
                                },

                                "& table thead th": {
                                    color:
                                        "#475467 !important",

                                    fontSize:
                                        "10px !important",

                                    fontWeight:
                                        "800 !important",

                                    letterSpacing:
                                        ".085em",
                                },

                                "& table tbody td": {
                                    borderBottom:
                                        "1px solid #F0F2F5 !important",

                                    verticalAlign:
                                        "middle",
                                },

                                "& table tbody tr:nth-of-type(even)": {
                                    background:
                                        "#FCFCFD",
                                },

                                "& table tbody tr:hover": {
                                    background:
                                        "#F5F8FF !important",

                                    boxShadow:
                                        "inset 3px 0 0 #175CD3",
                                },
                            }}
                        >
                            <DataTable
                                table={
                                    tableData
                                }
                                entriesPerPage={{
                                    defaultValue:
                                        10,

                                    entries: [
                                        10,
                                        25,
                                        50,
                                        100,
                                    ],
                                }}
                                canSearch={
                                    false
                                }
                                showTotalEntries
                                isSorted
                                noEndBorder
                            />
                        </MDBox>
                    )}
                </Card>
            </MDBox>

            {/* NEW RECONCILIATION */}
            <Dialog
                open={
                    openModal
                }
                onClose={
                    handleCloseModal
                }
                fullWidth
                maxWidth="lg"
                PaperProps={{
                    sx: {
                        borderRadius:
                            "10px",

                        overflow:
                            "hidden",

                        background:
                        COLORS.page,
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        p:
                            0,
                    }}
                >
                    <MDBox
                        p={{
                            xs:
                                2.5,

                            md:
                                3,
                        }}
                        sx={{
                            background:
                                "#0B1F3A",

                            color:
                            COLORS.white,
                        }}
                    >
                        <MDBox
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            gap={2}
                        >
                            <MDBox
                                display="flex"
                                alignItems="center"
                                gap={1.5}
                            >
                                <MDBox
                                    width={44}
                                    height={44}
                                    display="flex"
                                    justifyContent="center"
                                    alignItems="center"
                                    borderRadius="8px"
                                    sx={{
                                        background:
                                            "rgba(255,255,255,.13)",
                                    }}
                                >
                                    <CompareArrows />
                                </MDBox>

                                <MDBox>
                                    <MDTypography
                                        variant="h5"
                                        fontWeight="bold"
                                        sx={{
                                            color:
                                            COLORS.white,
                                        }}
                                    >
                                        {financeText(
                                            "New Reconciliation"
                                        )}
                                    </MDTypography>

                                    <MDTypography
                                        variant="caption"
                                        sx={{
                                            color:
                                                "rgba(255,255,255,.72)",
                                        }}
                                    >
                                        {financeText(
                                            "Select an account and balance date to calculate all financial movements up to that date."
                                        )}
                                    </MDTypography>
                                </MDBox>
                            </MDBox>

                            <Chip
                                label={
                                    financeText(
                                        "Draft"
                                    ).toUpperCase()
                                }
                                size="small"
                                sx={{
                                    color:
                                    COLORS.white,

                                    background:
                                        "rgba(255,255,255,.13)",

                                    border:
                                        "1px solid rgba(255,255,255,.15)",

                                    fontWeight:
                                        700,
                                }}
                            />
                        </MDBox>
                    </MDBox>
                </DialogTitle>

                <DialogContent
                    sx={{
                        p: {
                            xs:
                                2,

                            md:
                                3,
                        },
                    }}
                >
                    <Card
                        sx={
                            cardSx
                        }
                    >
                        <MDBox
                            p={2.5}
                        >
                            <MDBox
                                display="flex"
                                alignItems="center"
                                gap={1}
                                mb={2.5}
                            >
                                <Business
                                    sx={{
                                        color:
                                        COLORS.blue,
                                    }}
                                />

                                <MDBox>
                                    <MDTypography
                                        variant="h6"
                                        fontWeight="bold"
                                        sx={{
                                            color:
                                            COLORS.text,
                                        }}
                                    >
                                        {financeText(
                                            "Account & Balance Date"
                                        )}
                                    </MDTypography>

                                    <MDTypography
                                        variant="caption"
                                        sx={{
                                            color:
                                            COLORS.subtext,
                                        }}
                                    >
                                        {financeText(
                                            "Select the account, currency and date to calculate the balance up to that date."
                                        )}
                                    </MDTypography>
                                </MDBox>
                            </MDBox>

                            <Grid
                                container
                                spacing={2}
                                alignItems="stretch"
                            >
                                {/* PARTY TYPE */}
                                <Grid
                                    item
                                    xs={12}
                                    md={3}
                                >
                                    <FormControl
                                        fullWidth
                                        size="small"
                                        sx={
                                            commonSelectSx
                                        }
                                    >
                                        <InputLabel>
                                            {financeText(
                                                "Party Type"
                                            )}
                                        </InputLabel>

                                        <Select
                                            value={
                                                form.partyType
                                            }
                                            label={
                                                financeText(
                                                    "Party Type"
                                                )
                                            }
                                            onChange={(
                                                event
                                            ) =>
                                                handleFormChange(
                                                    "partyType",
                                                    event.target.value
                                                )
                                            }
                                        >
                                            {PARTY_TYPES.map(
                                                (
                                                    item
                                                ) => (
                                                    <MenuItem
                                                        key={
                                                            item.value
                                                        }
                                                        value={
                                                            item.value
                                                        }
                                                    >
                                                        {financeText(
                                                            item.label
                                                        )}
                                                    </MenuItem>
                                                )
                                            )}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                {/* ACCOUNT */}
                                <Grid
                                    item
                                    xs={12}
                                    md={3}
                                >
                                    <Autocomplete
                                        fullWidth
                                        size="small"
                                        loading={
                                            partyLoading
                                        }
                                        options={
                                            parties
                                        }
                                        value={
                                            selectedParty
                                        }
                                        onChange={(
                                            event,
                                            party
                                        ) =>
                                            handleFormChange(
                                                "partyId",
                                                party?._id ||
                                                ""
                                            )
                                        }
                                        getOptionLabel={(
                                            option
                                        ) =>
                                            option?.name ||
                                            ""
                                        }
                                        isOptionEqualToValue={(
                                            option,
                                            value
                                        ) =>
                                            String(
                                                option?._id
                                            ) ===
                                            String(
                                                value?._id
                                            )
                                        }
                                        noOptionsText={
                                            partyLoading
                                                ? financeText(
                                                    "Loading..."
                                                )
                                                : form.partyType ===
                                                "customer"
                                                    ? financeText(
                                                        "No customers found"
                                                    )
                                                    : financeText(
                                                        "No service providers found"
                                                    )
                                        }
                                        sx={
                                            commonAutocompleteSx
                                        }
                                        renderOption={(
                                            { key: optionKey, ...props },
                                            option
                                        ) => (
                                            <li
                                                {...props}
                                                key={`${option.partyType}-${option._id}`}
                                            >
                                                <MDBox
                                                    display="flex"
                                                    alignItems="center"
                                                    gap={1}
                                                    width="100%"
                                                >
                                                    <MDBox
                                                        width={30}
                                                        height={30}
                                                        display="flex"
                                                        alignItems="center"
                                                        justifyContent="center"
                                                        borderRadius="8px"
                                                        sx={{
                                                            background:
                                                            COLORS.blueLight,

                                                            color:
                                                            COLORS.blue,
                                                        }}
                                                    >
                                                        <Business
                                                            sx={{
                                                                fontSize:
                                                                    17,
                                                            }}
                                                        />
                                                    </MDBox>

                                                    <MDTypography
                                                        variant="button"
                                                        fontWeight="medium"
                                                    >
                                                        {
                                                            option.name
                                                        }
                                                    </MDTypography>
                                                </MDBox>
                                            </li>
                                        )}
                                        renderInput={(
                                            params
                                        ) => (
                                            <TextField
                                                {...params}
                                                size="small"
                                                label={
                                                    form.partyType ===
                                                    "customer"
                                                        ? financeText(
                                                            "Customer"
                                                        )
                                                        : financeText(
                                                            "Service Provider"
                                                        )
                                                }
                                                placeholder={
                                                    form.partyType ===
                                                    "customer"
                                                        ? financeText(
                                                            "Search customer"
                                                        )
                                                        : financeText(
                                                            "Search service provider"
                                                        )
                                                }
                                                InputProps={{
                                                    ...params.InputProps,

                                                    endAdornment: (
                                                        <>
                                                            {partyLoading ? (
                                                                <CircularProgress
                                                                    size={17}
                                                                />
                                                            ) : null}

                                                            {
                                                                params
                                                                    .InputProps
                                                                    .endAdornment
                                                            }
                                                        </>
                                                    ),
                                                }}
                                            />
                                        )}
                                    />
                                </Grid>

                                {/* CURRENCY */}
                                <Grid
                                    item
                                    xs={12}
                                    md={3}
                                >
                                    <FormControl
                                        fullWidth
                                        size="small"
                                        sx={
                                            commonSelectSx
                                        }
                                    >
                                        <InputLabel>
                                            {financeText(
                                                "Currency"
                                            )}
                                        </InputLabel>

                                        <Select
                                            value={
                                                form.currency
                                            }
                                            label={
                                                financeText(
                                                    "Currency"
                                                )
                                            }
                                            onChange={(
                                                event
                                            ) =>
                                                handleFormChange(
                                                    "currency",
                                                    event.target.value
                                                )
                                            }
                                        >
                                            {CURRENCIES.map(
                                                (
                                                    currency
                                                ) => (
                                                    <MenuItem
                                                        key={
                                                            currency
                                                        }
                                                        value={
                                                            currency
                                                        }
                                                    >
                                                        {
                                                            currency
                                                        }
                                                    </MenuItem>
                                                )
                                            )}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                {/* BALANCE DATE */}
                                <Grid
                                    item
                                    xs={12}
                                    md={3}
                                >
                                    <TextField
                                        fullWidth
                                        size="small"
                                        type="date"
                                        label={
                                            financeText(
                                                "Balance Date"
                                            )
                                        }
                                        InputLabelProps={{
                                            shrink:
                                                true,
                                        }}
                                        value={
                                            form.periodEnd
                                        }
                                        onChange={(
                                            event
                                        ) =>
                                            handleFormChange(
                                                "periodEnd",
                                                event.target.value
                                            )
                                        }
                                        sx={
                                            commonFieldSx
                                        }
                                    />
                                </Grid>
                            </Grid>
                        </MDBox>
                    </Card>

                    {error ? (
                        <Alert
                            severity="error"
                            sx={{
                                mt:
                                    2,

                                borderRadius:
                                    "8px",
                            }}
                        >
                            {error}
                        </Alert>
                    ) : null}

                    <MDBox
                        mt={2.5}
                        display="flex"
                        justifyContent="flex-end"
                    >
                        <MDButton
                            variant="contained"
                            color="info"
                            disabled={
                                calculateLoading ||
                                partyLoading
                            }
                            onClick={
                                handleCalculate
                            }
                            sx={{
                                minWidth:
                                    150,

                                color:
                                    "#FFFFFF !important",
                            }}
                        >
                            {calculateLoading ? (
                                <CircularProgress
                                    size={19}
                                    color="inherit"
                                />
                            ) : (
                                <>
                                    <AccountBalanceWallet
                                        sx={{
                                            mr:
                                                0.5,
                                        }}
                                    />

                                    {financeText(
                                        "Calculate"
                                    )}
                                </>
                            )}
                        </MDButton>
                    </MDBox>

                    {/* PREVIEW */}
                    {calculation ? (
                        <MDBox
                            mt={3}
                        >
                            <Card
                                sx={
                                    cardSx
                                }
                            >
                                <MDBox
                                    p={2.5}
                                >
                                    <MDBox
                                        display="flex"
                                        justifyContent="space-between"
                                        alignItems={{
                                            xs:
                                                "flex-start",

                                            md:
                                                "center",
                                        }}
                                        flexDirection={{
                                            xs:
                                                "column",

                                            md:
                                                "row",
                                        }}
                                        gap={1}
                                        mb={2.5}
                                    >
                                        <MDBox>
                                            <MDTypography
                                                variant="h6"
                                                fontWeight="bold"
                                                sx={{
                                                    color:
                                                    COLORS.text,
                                                }}
                                            >
                                                {financeText(
                                                    "Reconciliation Preview"
                                                )}
                                            </MDTypography>

                                            <MDTypography
                                                variant="caption"
                                                sx={{
                                                    color:
                                                    COLORS.subtext,
                                                }}
                                            >
                                                {calculation
                                                        ?.party
                                                        ?.name ||
                                                    selectedParty
                                                        ?.name ||
                                                    "-"}

                                                {" • "}

                                                {
                                                    form.currency
                                                }

                                                {" • "}

                                                {financeText(
                                                    "Balance Date"
                                                )}

                                                {": "}

                                                {formatDate(
                                                    form.periodEnd,
                                                    financeLanguage
                                                )}
                                            </MDTypography>
                                        </MDBox>

                                        <Chip
                                            label={`${transactionTable.length} ${financeText(
                                                "Transactions"
                                            )}`}
                                            sx={{
                                                background:
                                                COLORS.purpleLight,

                                                color:
                                                COLORS.purple,

                                                fontWeight:
                                                    700,
                                            }}
                                        />
                                    </MDBox>

                                    {/* CURRENT BALANCE */}
                                    <MDBox
                                        p={2.5}
                                        mb={2.5}
                                        borderRadius="10px"
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="space-between"
                                        sx={{
                                            background:
                                                "#F4F3FF",

                                            border:
                                                `1px solid ${COLORS.border}`,
                                        }}
                                    >
                                        <MDBox>
                                            <MDTypography
                                                variant="caption"
                                                fontWeight="bold"
                                                sx={{
                                                    color:
                                                    COLORS.subtext,

                                                    letterSpacing:
                                                        ".05em",
                                                }}
                                            >
                                                {financeText(
                                                    "CURRENT BALANCE"
                                                )}
                                            </MDTypography>

                                            <MDTypography
                                                variant="h4"
                                                fontWeight="bold"
                                                mt={0.5}
                                                sx={{
                                                    color:
                                                    COLORS.navy,
                                                }}
                                            >
                                                {formatMoney(
                                                    calculation.closingBalance,
                                                    form.currency,
                                                    financeLanguage
                                                )}
                                            </MDTypography>

                                            <MDTypography
                                                variant="caption"
                                                sx={{
                                                    color:
                                                    COLORS.subtext,
                                                }}
                                            >
                                                {financeText(
                                                    "Balance including all records up to the selected date."
                                                )}
                                            </MDTypography>
                                        </MDBox>

                                        <MDBox
                                            width={54}
                                            height={54}
                                            borderRadius="15px"
                                            display="flex"
                                            alignItems="center"
                                            justifyContent="center"
                                            sx={{
                                                background:
                                                    "#FFFFFF",

                                                color:
                                                COLORS.purple,

                                                boxShadow:
                                                    "0 5px 16px rgba(105,65,198,.10)",
                                            }}
                                        >
                                            <AccountBalanceWallet />
                                        </MDBox>
                                    </MDBox>

                                    {/* TRANSACTIONS UNTIL BALANCE DATE */}
                                    <Paper
                                        variant="outlined"
                                        sx={{
                                            borderRadius:
                                                "8px",

                                            overflow:
                                                "hidden",

                                            borderColor:
                                            COLORS.border,
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                overflowX:
                                                    "auto",
                                            }}
                                        >
                                            <Box
                                                component="table"
                                                sx={{
                                                    width:
                                                        "100%",

                                                    borderCollapse:
                                                        "collapse",

                                                    minWidth:
                                                        850,
                                                }}
                                            >
                                                <Box component="thead">
                                                    <Box
                                                        component="tr"
                                                        sx={{
                                                            background:
                                                                "#F8FAFC",
                                                        }}
                                                    >
                                                        {[
                                                            "Date",
                                                            "Type",
                                                            "Document",
                                                            "Description",
                                                            "Debit",
                                                            "Credit",
                                                            "Balance",
                                                        ].map(
                                                            (
                                                                heading
                                                            ) => (
                                                                <Box
                                                                    key={
                                                                        heading
                                                                    }
                                                                    component="th"
                                                                    sx={{
                                                                        px:
                                                                            2,

                                                                        py:
                                                                            1.5,

                                                                        fontSize:
                                                                            11,

                                                                        letterSpacing:
                                                                            ".04em",

                                                                        textTransform:
                                                                            "uppercase",

                                                                        color:
                                                                        COLORS.subtext,

                                                                        textAlign:
                                                                            [
                                                                                "Debit",
                                                                                "Credit",
                                                                                "Balance",
                                                                            ].includes(
                                                                                heading
                                                                            )
                                                                                ? "right"
                                                                                : "left",

                                                                        borderBottom:
                                                                            `1px solid ${COLORS.border}`,
                                                                    }}
                                                                >
                                                                    {financeText(
                                                                        heading
                                                                    )}
                                                                </Box>
                                                            )
                                                        )}
                                                    </Box>
                                                </Box>

                                                <Box component="tbody">
                                                    {transactionTable.length ===
                                                    0 ? (
                                                        <Box component="tr">
                                                            <Box
                                                                component="td"
                                                                colSpan={7}
                                                                sx={{
                                                                    p:
                                                                        5,

                                                                    textAlign:
                                                                        "center",
                                                                }}
                                                            >
                                                                <ReceiptLong
                                                                    sx={{
                                                                        fontSize:
                                                                            38,

                                                                        color:
                                                                            "#98A2B3",
                                                                    }}
                                                                />

                                                                <Typography
                                                                    variant="body2"
                                                                    color="text.secondary"
                                                                >
                                                                    {financeText(
                                                                        "No transactions found up to the selected balance date."
                                                                    )}
                                                                </Typography>
                                                            </Box>
                                                        </Box>
                                                    ) : (
                                                        transactionTable.map(
                                                            (
                                                                transaction,
                                                                index
                                                            ) => (
                                                                <Box
                                                                    component="tr"
                                                                    key={
                                                                        transaction.sourceId ||
                                                                        `${transaction.documentNo}-${index}`
                                                                    }
                                                                    sx={{
                                                                        "&:hover": {
                                                                            background:
                                                                                "#F9FAFB",
                                                                        },
                                                                    }}
                                                                >
                                                                    <Box
                                                                        component="td"
                                                                        sx={{
                                                                            px:
                                                                                2,

                                                                            py:
                                                                                1.6,

                                                                            borderBottom:
                                                                                `1px solid ${COLORS.border}`,
                                                                        }}
                                                                    >
                                                                        {formatDate(
                                                                            transaction.date,
                                                                            financeLanguage
                                                                        )}
                                                                    </Box>

                                                                    <Box
                                                                        component="td"
                                                                        sx={{
                                                                            px:
                                                                                2,

                                                                            py:
                                                                                1.6,

                                                                            borderBottom:
                                                                                `1px solid ${COLORS.border}`,
                                                                        }}
                                                                    >
                                                                        <Chip
                                                                            size="small"
                                                                            label={
                                                                                financeText(
                                                                                    transaction.sourceType ||
                                                                                    transaction.type ||
                                                                                    "-"
                                                                                )
                                                                            }
                                                                            sx={{
                                                                                fontWeight:
                                                                                    600,
                                                                            }}
                                                                        />
                                                                    </Box>

                                                                    <Box
                                                                        component="td"
                                                                        sx={{
                                                                            px:
                                                                                2,

                                                                            py:
                                                                                1.6,

                                                                            borderBottom:
                                                                                `1px solid ${COLORS.border}`,

                                                                            fontWeight:
                                                                                600,
                                                                        }}
                                                                    >
                                                                        {transaction.documentNo ||
                                                                            "-"}
                                                                    </Box>

                                                                    <Box
                                                                        component="td"
                                                                        sx={{
                                                                            px:
                                                                                2,

                                                                            py:
                                                                                1.6,

                                                                            borderBottom:
                                                                                `1px solid ${COLORS.border}`,

                                                                            color:
                                                                            COLORS.subtext,
                                                                        }}
                                                                    >
                                                                        {transaction.description ||
                                                                            "-"}
                                                                    </Box>

                                                                    <Box
                                                                        component="td"
                                                                        sx={{
                                                                            px:
                                                                                2,

                                                                            py:
                                                                                1.6,

                                                                            textAlign:
                                                                                "right",

                                                                            borderBottom:
                                                                                `1px solid ${COLORS.border}`,

                                                                            color:
                                                                            COLORS.blue,

                                                                            fontWeight:
                                                                                600,
                                                                        }}
                                                                    >
                                                                        {formatMoney(
                                                                            transaction.debit,
                                                                            form.currency,
                                                                            financeLanguage
                                                                        )}
                                                                    </Box>

                                                                    <Box
                                                                        component="td"
                                                                        sx={{
                                                                            px:
                                                                                2,

                                                                            py:
                                                                                1.6,

                                                                            textAlign:
                                                                                "right",

                                                                            borderBottom:
                                                                                `1px solid ${COLORS.border}`,

                                                                            color:
                                                                            COLORS.green,

                                                                            fontWeight:
                                                                                600,
                                                                        }}
                                                                    >
                                                                        {formatMoney(
                                                                            transaction.credit,
                                                                            form.currency,
                                                                            financeLanguage
                                                                        )}
                                                                    </Box>

                                                                    <Box
                                                                        component="td"
                                                                        sx={{
                                                                            px:
                                                                                2,

                                                                            py:
                                                                                1.6,

                                                                            textAlign:
                                                                                "right",

                                                                            borderBottom:
                                                                                `1px solid ${COLORS.border}`,

                                                                            fontWeight:
                                                                                700,

                                                                            color:
                                                                            COLORS.text,
                                                                        }}
                                                                    >
                                                                        {formatMoney(
                                                                            transaction.balance,
                                                                            form.currency,
                                                                            financeLanguage
                                                                        )}
                                                                    </Box>
                                                                </Box>
                                                            )
                                                        )
                                                    )}
                                                </Box>
                                            </Box>
                                        </Box>
                                    </Paper>
                                </MDBox>
                            </Card>
                        </MDBox>
                    ) : null}
                </DialogContent>

                <DialogActions
                    sx={{
                        px:
                            3,

                        py:
                            2,

                        background:
                        COLORS.white,

                        borderTop:
                            `1px solid ${COLORS.border}`,
                    }}
                >
                    <Button
                        onClick={
                            handleCloseModal
                        }
                        disabled={
                            calculateLoading ||
                            saveLoading
                        }
                        sx={{
                            color:
                            COLORS.subtext,
                        }}
                    >
                        {financeText(
                            "Cancel"
                        )}
                    </Button>

                    {calculation ? (
                        <MDButton
                            variant="contained"
                            color="info"
                            onClick={
                                handleSaveDraft
                            }
                            disabled={
                                saveLoading
                            }
                            sx={{
                                color:
                                    "#FFFFFF !important",

                                minWidth:
                                    145,
                            }}
                        >
                            {saveLoading ? (
                                <CircularProgress
                                    size={20}
                                    color="inherit"
                                />
                            ) : (
                                financeText(
                                    "Save Draft"
                                )
                            )}
                        </MDButton>
                    ) : null}
                </DialogActions>
            </Dialog>

            <Footer />
        </DashboardLayout>
    );
}

export default Reconciliations;